/**
 * 面包屑
 */
import { NoopArray } from '@wakeadmin/utils';
import { computed, defineComponent, unref, PropType } from 'vue';

import { useBayModel } from '@/hooks';
import { TreeNode } from '@/tree';
import { getMenuI18nKey, useT } from '@/utils';

import style from './Breadcrumb.scss?inline';

export interface CustomBreadcrumbItem {
  id?: string;
  title: string;
  onClick?: () => void;
}

export interface BreadCrumbItem extends Omit<CustomBreadcrumbItem, 'id'> {
  id: string | number;
  name?: string
  index: number;
  isLeaf: boolean;
}

export const Breadcrumb = defineComponent({
  name: 'BayLayoutBreadcrumb',
  styles: [style],
  props: {
    includeRoot: {
      type: Boolean,
      default: false,
    },
    includeLeaf: {
      type: Boolean,
      default: true,
    },
    hideOnSingle: {
      type: Boolean,
      default: true,
    },
    list: {
      type: Array as PropType<CustomBreadcrumbItem[]>,
      default: undefined,
    },
    /**
     * 拦截处理面包屑
     */
    afterCreated: {
      type: Function as PropType<(list: BreadCrumbItem[]) => void>,
      default: undefined,
    },
  },
  setup(props) {
    const bay = useBayModel();
    const t = useT();

    const nodes = computed(() => {
      const activeNode = bay.menu?.activeNode;
      if (activeNode == null) {
        return NoopArray;
      }

      let list: TreeNode[] = [];
      let current: TreeNode | undefined = activeNode;

      while (current) {
        if (current.url) {
          list.push(current);
        }

        current = current.parent;
      }

      list.reverse();

      if (!props.includeRoot) {
        list = list.slice(1);
      }

      if (!props.includeLeaf) {
        list = list.slice(0, -1);
      }

      return list;
    });

    const breadcrumb = computed<(TreeNode | CustomBreadcrumbItem)[]>(() => {
      return [...nodes.value, ...(props.list ?? NoopArray)];
    });

    const items = computed<BreadCrumbItem[]>(() => {
      const list = breadcrumb.value.map((i, idx) => {
        const isLeaf = idx === breadcrumb.value.length - 1;
        return {
          index: idx,
          isLeaf,
          name: TreeNode.isTreeNode(i) ? i.identifier : i.title,
          id: TreeNode.isTreeNode(i) ? i.uid : i.id ?? idx,
          title: TreeNode.isTreeNode(i) ? t(getMenuI18nKey(i.identifierPath), i.name) : i.title,
          onClick: () => {
            if (TreeNode.isTreeNode(i)) {
              if (!isLeaf) {
                bay.openTreeNode(i);
              }
            } else {
              i.onClick?.();
            }
          },
        };
      });

      props.afterCreated?.(list);

      return list;
    });

    return () => {
      const list = unref(items);

      if (list.length) {
        if (list.length === 1 && props.hideOnSingle) {
          return null;
        }

        return (
          <div class="bay-layout-breadcrumb">
            {list.map(i => {
              return (
                <div class={['bay-layout-breadcrumb__item', { leaf: i.isLeaf }]} key={i.id} onClick={i.onClick}>
                  <span class="bay-layout-breadcrumb__name">{i.title}</span>
                  {!i.isLeaf && <span class="bay-layout-breadcrumb__separator">/</span>}
                </div>
              );
            })}
          </div>
        );
      }

      return null;
    };
  },
});
